import { Box, Form, FormattedAddress, Grid, SelectInput, Stack, Typography } from '@garner-health/components-common';
import { useStringResource } from '@garner-health/lib-ui-content-management';
import { useFlags } from '@garner-health/lib-ui-feature-flags';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import constants from 'app-constants';
import React, { ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { ProfessionalLocation } from '~/clients';
import { WrappedGarnerMap } from '~/components/common/garner-map-wrapper';
import {
  ProviderDetailContextType,
  useProviderDetailDispatch,
  useProviderDetails,
} from '~/contexts/provider-detail-context';
import { useRumLoadVital } from '~/hooks';
import { useMapAssets } from '~/hooks/use-map-assets';

import { IconLabel } from '../common';
import { LocationInfo } from './location-info';
import { ProviderHours } from './provider-hours';

const AddressWithIcon = ({ lines, city, state, zipCode }: ProfessionalLocation) => {
  return (
    <IconLabel
      icon={<LocationOnOutlinedIcon />}
      body={
        <FormattedAddress
          address={{
            lines,
            city,
            state,
            zipCode,
          }}
        />
      }
    />
  );
};

const AddressWithoutIcon = ({ lines, city, state, zipCode }: ProfessionalLocation) => {
  return (
    <Stack paddingY="xs">
      <FormattedAddress
        address={{
          lines,
          city,
          state,
          zipCode,
        }}
      />
    </Stack>
  );
};

const ProviderLocationBody = ({ professional, facility, locationId }: ProviderDetailContextType) => {
  const { showProviderAffiliations } = useFlags();
  const dispatch = useProviderDetailDispatch();
  const locationLabel = useStringResource('providerDetails', 'locationSectionTitle');
  const facilityNameLabel = useStringResource('providerDetails', 'locationFacilityNameLabel');

  const form = useForm<{ location: string }>({ defaultValues: { location: locationId } });
  // Hold the map until manifest resolution settles so MapLibre isn't handed the
  // fallback URL mid-load (which fetches the legacy tile before the hashed one).
  // `isLoading` is only true on the first map mount of a session.
  const { tileUrl, spriteBaseUrl, isLoading } = useMapAssets();

  useRumLoadVital('provider_map_assets_loaded', isLoading);

  const location = facility
    ? { ...facility.location, acceptsNewPatients: undefined }
    : professional.locations.get(locationId);
  if (!location) return;

  // Options rendered in the dropdown menu when there are multiple locations
  const locationMenuOptions =
    professional &&
    Array.from(professional.locations.entries()).map(([key, loc]) => {
      return { value: key, label: <AddressWithoutIcon {...loc} /> };
    });

  const selectedLocationMap = new Map<string, ReactNode>();
  professional?.locations.forEach((loc, key) => {
    selectedLocationMap.set(key, <AddressWithIcon {...loc} />);
  });

  const renderValue = (value: string) => selectedLocationMap.get(value);
  const onLocationSelectChange = (value: string) => dispatch({ type: 'updateLocation', locationId: value });

  const resolvedAffiliatedFacilityName =
    showProviderAffiliations && !facility ? location.facilityName || location.name : undefined;

  const facilityNameDisplay = resolvedAffiliatedFacilityName ? (
    <IconLabel
      icon={<BusinessOutlinedIcon aria-label={facilityNameLabel} />}
      body={<Typography variant="body1">{resolvedAffiliatedFacilityName}</Typography>}
    />
  ) : null;

  const locationInfo = (
    <LocationInfo
      phoneNumber={location.phoneNumber}
      faxNumber={location.faxNumber}
      //NB: We only show limited availability when `acceptsNewPatients` is confirmed `false`.
      //Having acceptsNewPatients=undefined is treated as limitedAvailability=false
      limitedAvailability={location.acceptsNewPatients === false}
    />
  );

  return (
    <>
      {locationMenuOptions && locationMenuOptions.length > 1 ? (
        <Form form={form}>
          <Stack spacing="sm">
            <Grid>
              <Grid.Item xs={24} md={4}>
                <SelectInput
                  aria-label={locationLabel}
                  size="small"
                  allowClear={false}
                  label=""
                  options={locationMenuOptions}
                  name="location"
                  onChange={onLocationSelectChange}
                  renderValue={renderValue}
                />
              </Grid.Item>
            </Grid>
            <Stack paddingX="sm">
              {facilityNameDisplay}
              {locationInfo}
            </Stack>
          </Stack>
        </Form>
      ) : (
        <Stack spacing="sm">
          <AddressWithIcon {...location} />
          {facilityNameDisplay}
          {locationInfo}
        </Stack>
      )}

      <Box paddingBottom="xs">
        <div style={{ height: 300, borderRadius: 8, overflow: 'hidden' }}>
          {!isLoading && (
            <WrappedGarnerMap
              tileUrl={tileUrl}
              spriteBaseUrl={spriteBaseUrl}
              assetBaseUrl={constants.map.assetBaseUrl}
              center={location.position}
              minZoom={15}
              maxZoom={15}
              showCenterMarker
            />
          )}
        </div>
      </Box>

      {!!location.hours?.length && <ProviderHours hours={location.hours} />}
    </>
  );
};

export const ProviderLocation = () => {
  const details = useProviderDetails();
  if (!details) return;
  return <ProviderLocationBody {...details} />;
};
